import React, { FC, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { setActiveModal } from '../../actions/modalActions';
import { CHAT_AUTHENTICATION_MODAL } from '../../constants/documentTypes/modals/modals';
import theme from '../../constants/theme';
import { getCurrentLangPretty } from '../../helpers/url/urlHelpers';
import { ReactComponent as Njall } from '../../icons/njall-start-icon-normal.svg';
import * as StyledChatBubble from './chatBubble.styles';
import { watsonIS } from './watson_is';

interface Props {}
const namespaceName = () => `watsonAssistantChat_${Date.now()}_${Math.random().toString(16).slice(2)}`;

const CHAT_POLL_INTERVAL_MS = 3000;
// Zendesk is only wired up for a subset of conversations, so most sessions never see zChat at all.
// Give up after five minutes instead of polling for the lifetime of the tab.
const CHAT_POLL_MAX_ATTEMPTS = 100;

/**
 * Reads the IBM web chat session id out of sessionStorage.
 *
 * `sessionStorage.key(i)` returns null once `i` reaches the end of the store, and the previous
 * fixed three-iteration loop walked straight off that end whenever the visitor had fewer than three
 * session keys, throwing on `null.includes(...)`.
 */
const findIbmSessionId = (): string | null => {
	for (let i = 0; i < window.sessionStorage.length; i++) {
		const key = window.sessionStorage.key(i);

		if (key && key.includes('IBMuid')) return key.substring('IBM_WAC_SESSION_'.length).split(':')[0];
	}

	return null;
};

const ChatBubble: FC<Props> = () => {
	const dispatch = useDispatch<any>();
	const namespaceRef = useRef<string>(namespaceName());
	const scriptAddedRef = useRef<boolean>(false);
	const disposersRef = useRef<Array<() => void>>([]);

	// Watson chat
	useEffect(() => {
		if (scriptAddedRef.current) return;
		scriptAddedRef.current = true;

		const disposers = disposersRef.current;

		// @ts-ignore
		window.watsonAssistantChatOptions = {
			integrationID:
				getCurrentLangPretty() === 'is'
					? '2c6eae08-9668-4017-8979-3dfa0e4fc31f'
					: 'f64ca997-3159-447a-8d9a-2d366e7f0ceb', // The ID of this integration.
			region: 'eu-gb', // The region your integration is hosted in.
			serviceInstanceID: '46b8080a-287f-4c13-a2ae-0f431955bb31', // The ID of your service instance.
			namespace: namespaceRef.current,
			// Hide default launcher
			showLauncher: false,

			onLoad: function (instance) {
				if (!instance) {
					// IBM calls onLoad with nothing when its own config fetch fails. There is no recovery
					// from our side, and it happens often enough that logging it as an error is just noise.
					console.warn('Watson Assistant instance not available; chat stays hidden.');
					return;
				}

				if (getCurrentLangPretty() === 'is' && typeof instance.updateLanguagePack === 'function') {
					instance.updateLanguagePack(watsonIS);
				}

				instance.updateCSSVariables({
					'$ui-background': theme.colors.white,
					$focus: theme.colors.primaryDark,
					'$hover-primary': theme.colors.primaryDark,
					'$interactive-01': theme.colors.primary,
					'$link-01': theme.colors.secondary,
					// '$ui-03': theme.colors.secondary,
					'$active-ui': theme.colors.secondaryDark,
				});

				// Use custom launcher
				const button = document.querySelector('#bubble');
				if (!button) {
					console.warn("Watson Assistant launcher element '#bubble' not found.");
					return;
				}

				instance.render();

				const onButtonClick = () => {
					instance.toggleOpen();
				};

				button.addEventListener('click', onButtonClick);
				disposers.push(() => button.removeEventListener('click', onButtonClick));

				// ElectronicId authentication trigger
				const authenticationTrigger = [
					'Vinsamlegast sláðu inn símanúmer.',
					'Please type in your phone number.',
				];
				let attempts = 0;

				const chatFindInterval = window.setInterval(() => {
					attempts += 1;

					//@ts-ignore
					const zChat = window.zChat;

					if (!zChat || !zChat.getVisitorInfo()) {
						if (attempts >= CHAT_POLL_MAX_ATTEMPTS) window.clearInterval(chatFindInterval);
						return;
					}

					window.clearInterval(chatFindInterval);

					zChat.on('chat', eventData => {
						if (eventData.type !== 'chat.msg' || !authenticationTrigger.includes(eventData.msg)) return;

						const sessionId = findIbmSessionId();
						if (!sessionId) return;

						if (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) instance?.closeWindow();

						dispatch(setActiveModal(CHAT_AUTHENTICATION_MODAL, sessionId));
					});
				}, CHAT_POLL_INTERVAL_MS);

				disposers.push(() => window.clearInterval(chatFindInterval));
			},
		};

		const timeoutId = window.setTimeout(function () {
			if (document.getElementById('watson-assistant-chat-script')) return;
			const t = document.createElement('script');
			t.id = 'watson-assistant-chat-script';
			t.src = 'https://web-chat.global.assistant.watson.appdomain.cloud/loadWatsonAssistantChat.js';
			// IBM's CDN drops this request regularly. Remove the tag and clear the flag so a later mount
			// gets another attempt, instead of leaving a dead script element that blocks every retry.
			t.onerror = () => {
				console.warn('Watson Assistant chat script failed to load.');
				t.remove();
				scriptAddedRef.current = false;
			};
			document.head.appendChild(t);
		});

		disposers.push(() => window.clearTimeout(timeoutId));

		return () => {
			disposers.forEach(dispose => dispose());
			disposersRef.current = [];
		};
	}, [dispatch]);

	return (
		<StyledChatBubble.Wrapper>
			<StyledChatBubble.BubbleWrapper id="bubble">
				<Njall />
			</StyledChatBubble.BubbleWrapper>
		</StyledChatBubble.Wrapper>
	);
};

export default ChatBubble;
